// Parses the result page URL: either the Action's test deny (IdP sign-in worked)
// or an Auth0 error. The URL can be crafted, so values are only displayed.
import { langageSelectOptionsWithTestLocales } from '../../../../utilities/localization';

/**
 * redirect_uri query param with the admin's language, as a private window has no
 * access to the stored choice. Auth0 keeps the query on error redirects too.
 */
export const IDP_TEST_LANGUAGE_PARAM = 'lng';

const PICKER_LANGUAGES = langageSelectOptionsWithTestLocales.map(o => o.value);

/** The requested language, if a picker value: it ends up in a file path. */
export const resultPageLanguage = (search: string): string | undefined => {
    const language = new URLSearchParams(search).get(IDP_TEST_LANGUAGE_PARAM);
    return language && PICKER_LANGUAGES.includes(language)
        ? language
        : undefined;
};

/** Must match the Action's IDP_TEST_DENY_PREFIX (bb-inject-jwt-claims). */
export const IDP_TEST_DENY_PREFIX =
    'IdP test sign-in, expected denial (not an error): ';

/** Failure explanation, matched on Auth0's SAML validation messages (IdP-independent). */
export type IdpTestHint =
    | 'connectionDisabled'
    /** An attribute name ending in "/" breaks every sign-in. */
    | 'emptyAttributeName'
    | 'certificate'
    | 'audience'
    | 'acs'
    | 'clock'
    | 'mapping'
    /** Denied, but not by the test branch. */
    | 'deniedOther'
    /** Auth0 issued a code: the Action didn't deny. */
    | 'notDenied'
    /** The test branch ran but couldn't build its result. */
    | 'testError'
    | 'generic';

export type IdpTestResult =
    | {
          kind: 'success';
          idp?: string;
          connection?: string;
          /** The username to enter in Create User. */
          loginName?: string;
          /** Email or name, for display. */
          username?: string;
          userId?: string;
          attributes: [string, string][];
          /** Attributes left out to keep the URL short. */
          omittedAttributes?: number;
      }
    | {
          kind: 'failure';
          idp?: string;
          hint: IdpTestHint;
          error?: string;
          description?: string;
      }
    | { kind: 'none' };

// First match wins. Matched on text, not code: certificate and audience errors
// come as access_denied, like the test deny. Observed: connectionDisabled,
// emptyAttributeName, certificate, audience, mapping. Unconfirmed: acs (a wrong
// ACS usually never reaches Auth0) and clock.
const HINT_RULES: [IdpTestHint, RegExp][] = [
    // Also a deleted or never-created connection ("not found").
    ['connectionDisabled', /connection.*(not enabled|disabled|not found)/i],
    // Auth0 keeps the part after the last "/" and fails on the empty name (error 56).
    ['emptyAttributeName', /empty update path/i],
    ['certificate', /thumbprint|signature|certificate/i],
    ['audience', /audience/i],
    ['acs', /destination|recipient|assertionconsumerservice/i],
    ['clock', /expired|notonorafter|notbefore|not yet valid/i],
    ['mapping', /user_id|mapping/i],
];

const asString = (value: unknown): string | undefined =>
    typeof value === 'string' && value.length > 0 ? value : undefined;

const positiveInteger = (value: unknown): number | undefined =>
    Number.isInteger(value) && (value as number) > 0
        ? (value as number)
        : undefined;

const parseJson = (value: string): unknown => {
    try {
        return JSON.parse(value);
    } catch {
        return undefined;
    }
};

const attributesOf = (value: unknown): [string, string][] =>
    typeof value === 'object' && value !== null && !Array.isArray(value)
        ? Object.entries(value as Record<string, unknown>).filter(
              // Empty values kept: the admin should see the IdP sends them blank.
              (entry): entry is [string, string] =>
                  entry[0].length > 0 && typeof entry[1] === 'string',
          )
        : [];

const hintFor = (
    error: string | undefined,
    description: string | undefined,
): IdpTestHint => {
    const text = description ?? '';
    const rule = HINT_RULES.find(([, pattern]) => pattern.test(text));
    if (rule) return rule[0];
    if (error === 'access_denied') return 'deniedOther';
    return 'generic';
};

export const parseIdpTestResult = (search: string): IdpTestResult => {
    const params = new URLSearchParams(search);
    const idp = asString(params.get('idp'));
    const error = asString(params.get('error'));
    const description = asString(params.get('error_description'));

    if (params.has('code')) {
        return { kind: 'failure', idp, hint: 'notDenied' };
    }

    if (!error && !description) {
        return { kind: 'none' };
    }

    if (description?.startsWith(IDP_TEST_DENY_PREFIX)) {
        const payload = parseJson(
            description.slice(IDP_TEST_DENY_PREFIX.length),
        ) as Record<string, unknown> | undefined;

        if (payload?.reason === 'IDP_TEST_OK') {
            return {
                kind: 'success',
                idp,
                connection: asString(payload.connection),
                loginName: asString(payload.loginName),
                username: asString(payload.username),
                userId: asString(payload.userId),
                attributes: attributesOf(payload.attributes),
                omittedAttributes: positiveInteger(payload.omittedAttributes),
            };
        }

        return {
            kind: 'failure',
            idp,
            hint:
                payload?.reason === 'IDP_TEST_ERROR' ? 'testError' : 'generic',
            error,
            description,
        };
    }

    return {
        kind: 'failure',
        idp,
        hint: hintFor(error, description),
        error,
        description,
    };
};
